// Module 6 / Slice 5 — Vehicle list screen.
//
// Read-first paginated list of crewing.vehicle rows; filters compose
// status (multi-select) + org_unit_id + vehicle_type_id (typeahead from
// M3 + M6 ungated for now). Each row links to the detail screen via the
// TanStack Router /admin/crewing/vehicles/$vehicleId route.
//
// Per MODULE_6_PLAN.md §10 the SPA stays behind the
// `crewing.admin_ui.enabled` feature flag — wiring happens at the
// AppShell nav level (route remains reachable for power users).
//
// PATTERNS.md §2.2 (screen composition) + §2.5 (data-testid naming).

import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { crewingApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";

type VehicleStatus = "active" | "out_of_service" | "retired";

const STATUS_OPTIONS: readonly VehicleStatus[] = ["active", "out_of_service", "retired"];

export function VehicleListScreen(): JSX.Element {
  const { t } = useI18n();
  const [statusFilter, setStatusFilter] = useState<VehicleStatus | "">("");
  const [orgUnitId, setOrgUnitId] = useState<string>("");
  const [vehicleTypeId, setVehicleTypeId] = useState<string>("");

  const query = useQuery({
    queryKey: ["crewing", "vehicles", { statusFilter, orgUnitId, vehicleTypeId }] as const,
    queryFn: async () => {
      const response = await crewingApiClient.GET("/crewing/vehicles", {
        params: {
          query: {
            limit: 200,
            includeArchived: false,
            ...(statusFilter !== "" ? { status: [statusFilter] } : {}),
            ...(orgUnitId.trim().length > 0 ? { orgUnitId: orgUnitId.trim() } : {}),
            ...(vehicleTypeId.trim().length > 0
              ? { vehicleTypeId: vehicleTypeId.trim() }
              : {}),
          },
        },
      });
      if (!response.data) {
        throw new Error("crewing_vehicles_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div data-testid="vehicle-list-screen">
      <h1>{t("crewing.vehicles.heading")}</h1>
      <p className="rc-help">{t("crewing.vehicles.description")}</p>

      <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <label htmlFor="vehicle-filter-status">
          {t("crewing.vehicles.filter.status")}{" "}
          <select
            id="vehicle-filter-status"
            data-testid="vehicle-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value as VehicleStatus | "") ?? "")}
            style={{ marginLeft: "0.5rem" }}
          >
            <option value="">{t("crewing.vehicles.filter.status.all")}</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`crewing.vehicles.filter.status.${s}`)}
              </option>
            ))}
          </select>
        </label>

        <label htmlFor="vehicle-filter-org-unit">
          {t("crewing.vehicles.filter.orgUnit")}{" "}
          <input
            id="vehicle-filter-org-unit"
            data-testid="vehicle-filter-org-unit"
            type="text"
            value={orgUnitId}
            placeholder={t("crewing.vehicles.filter.orgUnit.placeholder")}
            onChange={(e) => setOrgUnitId(e.target.value)}
            style={{ marginLeft: "0.5rem" }}
          />
        </label>

        <label htmlFor="vehicle-filter-type">
          {t("crewing.vehicles.filter.vehicleType")}{" "}
          <input
            id="vehicle-filter-type"
            data-testid="vehicle-filter-type"
            type="text"
            value={vehicleTypeId}
            placeholder={t("crewing.vehicles.filter.vehicleType.placeholder")}
            onChange={(e) => setVehicleTypeId(e.target.value)}
            style={{ marginLeft: "0.5rem" }}
          />
        </label>
      </div>

      {query.isLoading && <p>{t("crewing.vehicles.loading")}</p>}
      {query.isError && (
        <p
          role="alert"
          data-testid="vehicle-list-error"
          style={{ color: "var(--rc-color-danger)" }}
        >
          {(query.error as Error).message}
        </p>
      )}
      {query.data && (query.data.items ?? []).length === 0 && (
        <p data-testid="vehicle-list-empty">{t("crewing.vehicles.empty")}</p>
      )}
      {query.data && (query.data.items ?? []).length > 0 && (
        <table className="rc-table" data-testid="vehicle-list-table">
          <thead>
            <tr>
              <th>{t("crewing.vehicles.col.name")}</th>
              <th>{t("crewing.vehicles.col.identifier")}</th>
              <th>{t("crewing.vehicles.col.status")}</th>
              <th>{t("crewing.vehicles.col.orgUnit")}</th>
              <th>{t("crewing.vehicles.col.vehicleType")}</th>
            </tr>
          </thead>
          <tbody>
            {(query.data.items ?? []).map((row) => (
              <tr key={row.id} data-testid={`vehicle-row-${row.id}`}>
                <td>
                  <Link
                    to="/admin/crewing/vehicles/$vehicleId"
                    params={{ vehicleId: row.id }}
                    data-testid={`vehicle-link-${row.id}`}
                  >
                    {row.name}
                  </Link>
                </td>
                <td data-testid={`vehicle-identifier-${row.id}`}>{row.identifier}</td>
                <td>
                  <span data-testid={`vehicle-status-${row.id}`}>{row.status}</span>
                </td>
                <td>{row.orgUnitId}</td>
                <td>{row.vehicleTypeId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
